import api from "@/lib/api";
import { toast } from "sonner";
import { viewBlob } from "@/components/FileViewer";

export const SIZE_OPTIONS = [
  { value: "avery-5395", label: 'Name badge · 3⅜ × 2⅓" (Avery 5395 / 74459)', w: 3.375, h: 2.333 },
  { value: "4x3", label: 'Label · 4 × 3"', w: 4, h: 3 },
  { value: "brother-62x100", label: "Brother DK · 62 × 100 mm", w: 3.94, h: 2.44 },
  { value: "dymo-30256", label: 'Dymo 30256 · 4 × 2⅓"', w: 4, h: 2.31 },
  { value: "4x6", label: 'Thermal · 4 × 6"', w: 4, h: 6 },
  { value: "custom", label: "Custom size…" },
];

export const SAMPLE_VISIT = {
  id: "sample-badge", full_name: "Jane Smith", company: "Acme Corp", host_name: "Mr. Johnson", purpose: "Parent meeting",
  visitor_type: "visitor", vehicle_plate: "ABC-1234", checked_in_at: new Date().toISOString(),
};

export function badgeDims(cfg) {
  const preset = SIZE_OPTIONS.find((o) => o.value === cfg.size && o.w);
  return preset ? { w: preset.w, h: preset.h } : { w: Number(cfg.width_in) || 3.375, h: Number(cfg.height_in) || 2.333 };
}

const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
const pick = (v) => ({ id: v.id || null, full_name: v.full_name, company: v.company || "", host_name: v.host_name || "", purpose: v.purpose || "", visitor_type: v.visitor_type || "visitor", vehicle_plate: v.vehicle_plate || "", checked_in_at: v.checked_in_at || new Date().toISOString() });
const normalize = (cfg) => cfg ? { ...cfg, width_in: Number(cfg.width_in), height_in: Number(cfg.height_in), font_scale: Number(cfg.font_scale) } : undefined;

export async function fetchBadge(visit, cfg, format = "pdf") {
  const { data } = await api.post(`/badges/render.${format}`, { visit: pick(visit), settings: normalize(cfg), tz: tz() }, { responseType: "blob" });
  return data;
}

const isSafari = () => /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);
const SILENT_KEY = "vlog_silent_print";

// "1" once a print() returned instantly (Chrome --kiosk-printing), "0" after a print that showed the dialog, null = unknown.
export const silentPrintStatus = () => localStorage.getItem(SILENT_KEY);
export const isChromium = () => !!window.chrome && !isSafari();

// The PDF page equals the label size, so the print dialog uses the right paper size and Safari adds no headers/footers.
// Resolves to { silent: boolean|null } — silent is inferred from how long print() blocked (the dialog is modal).
export async function printBadge(visit, cfg) {
  let blob;
  try {
    blob = await fetchBadge(visit, cfg, "pdf");
  } catch {
    toast.error("Could not generate the badge");
    return { silent: null };
  }
  if (isSafari()) {
    // Safari can't print a PDF from a hidden frame — show it in the viewer where the user can print/open it.
    viewBlob(blob, { title: "Visitor badge", filename: "visitor-badge.pdf" });
    return { silent: false };
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
    frame.src = url;
    frame.onload = () => {
      setTimeout(() => {
        let silent = null;
        try {
          const t0 = performance.now();
          frame.contentWindow.focus();
          frame.contentWindow.print();
          silent = performance.now() - t0 < 1200;
          localStorage.setItem(SILENT_KEY, silent ? "1" : "0");
        } catch {
          viewBlob(blob, { title: "Visitor badge", filename: "visitor-badge.pdf" });
        }
        resolve({ silent });
      }, 300);
      setTimeout(() => { frame.remove(); URL.revokeObjectURL(url); }, 60000);
    };
    document.body.appendChild(frame);
  });
}
